import React, { useCallback, useEffect, useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, authToken } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { ukWhen } from "@/lib/time";
import {
  Btn, CellMain, CellSub, Chips, DeskSupport, Divider, Empty, Inp, Kv, Note, PageBody, PageHead, Panel,
  RailCard, Sec, St, StatStrip, Tb, Td, Th,
} from "@/components/portal/crm/kit";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const num = { fontVariantNumeric: "tabular-nums" };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September",
  "October", "November", "December"];
const copy = async (text, what = "Link copied") => {
  try { await navigator.clipboard.writeText(text); toast.success(what); } catch { toast.error("Copy failed"); }
};

/** The media partner's control room: a link per channel, the funnel on each, what it cost, and the
    conversion fed back to the ad platform. */
export default function Channels() {
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() + 1 });
  const [d, setD] = useState(null);
  const [form, setForm] = useState({ name: "", platform: "meta", utm_content: "" });
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState(null);
  const [pendingArchive, setPendingArchive] = useState(null);

  const load = useCallback(() => {
    api.get(`/refer/campaign/overview?year=${ym.y}&month=${ym.m}`)
      .then((r) => setD(r.data)).catch(() => setD(null));
  }, [ym]);
  useEffect(() => { load(); }, [load]);

  const shift = (by) => setYm(({ y, m }) => {
    const n = m + by;
    return n < 1 ? { y: y - 1, m: 12 } : n > 12 ? { y: y + 1, m: 1 } : { y, m: n };
  });

  const add = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) { toast.error("Give the channel a name you'll recognise"); return; }
    setBusy(true);
    try {
      const r = await api.post("/refer/campaign/links", {
        name: form.name.trim(), platform: form.platform,
        utm_content: form.utm_content.trim() || null,
      });
      toast.success(`${r.data.name} is live — paste ${r.data.url.replace(/^https?:\/\//, "")} into your ad`);
      setForm({ name: "", platform: form.platform, utm_content: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't create that channel"); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    const row = pendingArchive;
    if (!row) return;
    setPendingArchive(null);
    try {
      await api.patch(`/refer/campaign/links/${row.id}`, { archived: !row.archived });
      toast.success(row.archived ? "Channel back on" : `${row.name} archived — the link is now dead`);
      load();
    } catch { toast.error("That didn't go through"); }
  };

  const t = d?.totals || {};
  const live = (d?.rows || []).filter((r) => !r.archived);
  const platforms = d?.platforms || [];

  return (
    <>
      <PageHead
        eyebrow="Media partner"
        title="Your channels"
        lede="A link per platform, per campaign, per creative — with the click ids kept, so a funded case traces back to the exact ad. Type in what you spent and the economics work themselves out."
        testId="channels-head"
        meta={<>
          <Btn sm onClick={() => shift(-1)} testId="channels-prev">←</Btn>
          <span style={{ padding: "0 8px", fontWeight: 700 }} data-testid="channels-period">{d?.period || `${MONTHS[ym.m - 1]} ${ym.y}`}</span>
          <Btn sm onClick={() => shift(1)} testId="channels-next">→</Btn>
        </>}
      />

      <PageBody
        rail={
          <>
            <DeskSupport body="Creative checks, lender appetite, what's converting across the network. Ask us before you scale spend." />

            <RailCard title="This month" testId="channels-rail-month">
              <Kv rows={[
                ["Clicks", String(t.clicks ?? 0)],
                ["Applications", String(t.submitted ?? 0)],
                ["Click → app", t.click_to_app_pct != null ? `${t.click_to_app_pct}%` : "—"],
                ["Funded", String(t.funded ?? 0)],
                ["Spend", gbp(t.spend_gbp || 0)],
                ["Earnings", gbp(t.earnings_gbp || 0), { style: { fontWeight: 800 } }],
                ["ROAS", t.roas != null ? `${t.roas}×` : "—", t.roas >= 1 ? { style: { color: "var(--green)", fontWeight: 700 } } : undefined],
              ]} />
            </RailCard>

            <Note testId="channels-privacy-note">
              We never put your pixel on our application form, and we never hand over an applicant's
              details. You get the click id back instead — enough to optimise, nothing that makes you a
              data controller.
            </Note>
          </>
        }
      >
        <StatStrip
          testId="channels-stats"
          items={[
            { k: "Clicks", v: String(t.clicks ?? 0), h: `${t.started ?? 0} started the form`, testId: "channels-stat-clicks" },
            { k: "Applications", v: String(t.submitted ?? 0), h: t.cost_per_application ? `${gbp(t.cost_per_application)} each` : "spend not entered yet", testId: "channels-stat-apps" },
            { k: "Funded", v: String(t.funded ?? 0), h: t.cost_per_funded ? `${gbp(t.cost_per_funded)} cost per funded` : "—", tone: t.funded ? "green" : undefined, testId: "channels-stat-funded" },
            { k: "ROAS", v: t.roas != null ? `${t.roas}×` : "—", h: `${gbp(t.earnings_gbp || 0)} earned on ${gbp(t.spend_gbp || 0)}`, tone: t.roas >= 1 ? "green" : undefined, testId: "channels-stat-roas" },
          ]}
        />

        <Sec title="Your channels" sub="One link per thing you want to judge separately. The UTMs are already on it.">
          <Panel pad={false} testId="channels-panel"
                 footer={<span>Every tap is logged with whatever the platform appended — <b>fbclid</b>, <b>gclid</b>, <b>ttclid</b>, <b>li_fat_id</b> — plus your own <b>sub1</b>. Add <code>&amp;sub1=adset-name</code> to any of these links and it comes back on the export.</span>}>
            <form onSubmit={add} className="seat-form" data-testid="channel-form">
              <Inp placeholder="Channel name — e.g. Meta · retargeting · video A" value={form.name}
                   onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="channel-name" />
              <select className="inp" value={form.platform} data-testid="channel-platform"
                      onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))}>
                {platforms.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
              <Inp placeholder="Creative / ad (optional)" value={form.utm_content}
                   onChange={(e) => setForm((f) => ({ ...f, utm_content: e.target.value }))} data-testid="channel-content" />
              <Btn kind="primary" type="submit" disabled={busy} testId="channel-add">{busy ? "Minting…" : "New channel"}</Btn>
            </form>

            {d === null ? <Empty testId="channels-loading">Reading your channels…</Empty>
              : live.length === 0 ? (
                <Empty testId="channels-empty">
                  No channels yet. Name your first one above — you'll get a short link to paste straight into the ad.
                </Empty>
              ) : (
                <Tb testId="channels-table" head={<>
                  <Th style={{ width: "26%" }}>Channel</Th>
                  <Th className="r" style={{ width: "8%" }}>Clicks</Th>
                  <Th className="r" style={{ width: "8%" }}>Apps</Th>
                  <Th className="r" style={{ width: "8%" }}>Funded</Th>
                  <Th className="r" style={{ width: "12%" }}>Spend</Th>
                  <Th className="r" style={{ width: "10%" }}>Per app</Th>
                  <Th className="r" style={{ width: "11%" }}>Earned</Th>
                  <Th className="r" style={{ width: "7%" }}>ROAS</Th>
                  <Th className="r" style={{ width: "10%" }}>&nbsp;</Th>
                </>}>
                  {live.map((r) => (
                    <tr key={r.id} data-testid={`channel-row-${r.id}`}>
                      <Td className="lead">
                        <CellMain>{r.name}</CellMain>
                        <CellSub>{r.platform_label}{r.destination === "funding" ? " · explainer page" : ""}</CellSub>
                        {r.url ? (
                          <CellSub>
                            <button type="button" onClick={() => copy(r.url)} data-testid={`channel-copy-${r.id}`}
                                    style={linkBtn}>{r.url.replace(/^https?:\/\//, "")}</button>
                          </CellSub>
                        ) : <CellSub>Untagged traffic on your main link</CellSub>}
                      </Td>
                      <Td className="r"><CellMain style={num}>{r.clicks}</CellMain>{r.started ? <CellSub style={{ textAlign: "right" }}>{r.started} started</CellSub> : null}</Td>
                      <Td className="r"><CellMain style={num}>{r.submitted}</CellMain>{r.click_to_app_pct != null ? <CellSub style={{ textAlign: "right" }}>{r.click_to_app_pct}%</CellSub> : null}</Td>
                      <Td className="r"><CellMain style={{ ...num, color: r.funded ? "var(--green)" : undefined, fontWeight: r.funded ? 700 : 500 }}>{r.funded}</CellMain></Td>
                      <Td className="r"><SpendCell row={r} ym={ym} onSaved={load} /></Td>
                      <Td className="r"><CellMain style={{ ...num, color: "var(--mut)", fontWeight: 500 }}>{r.cost_per_application ? gbp(r.cost_per_application) : "—"}</CellMain>{r.cost_per_funded ? <CellSub style={{ textAlign: "right" }}>{gbp(r.cost_per_funded)} / funded</CellSub> : null}</Td>
                      <Td className="r"><CellMain style={{ ...num, fontWeight: r.earnings_gbp ? 800 : 500 }}>{r.earnings_gbp ? gbp(r.earnings_gbp) : "—"}</CellMain></Td>
                      <Td className="r">{r.roas != null
                        ? <St tone={r.roas >= 1 ? "mint" : "slate"}>{r.roas}×</St>
                        : <CellSub style={{ textAlign: "right" }}>—</CellSub>}</Td>
                      <Td className="r">
                        {r.url ? (
                          <div style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                            <Btn sm onClick={() => setQr(qr === r.id ? null : r.id)} testId={`channel-qr-${r.id}`}>QR</Btn>
                            <Btn sm onClick={() => setPendingArchive(r)} testId={`channel-archive-${r.id}`}>Archive</Btn>
                          </div>
                        ) : null}
                        {qr === r.id && r.url ? (
                          <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }} data-testid={`channel-qr-img-${r.id}`}>
                            <QRCodeCanvas value={r.url} size={96} level="M" includeMargin bgColor="#ffffff" fgColor="#0B1220" />
                          </div>
                        ) : null}
                      </Td>
                    </tr>
                  ))}
                </Tb>
              )}
          </Panel>
        </Sec>

        <Sec title="Feed it back to the platforms" sub="Your ad accounts optimise on conversions. We'll hand them one the moment a case qualifies or funds — click id only, never a person.">
          <Postback data={d} onSaved={load} ym={ym} />
        </Sec>

        <Sec title="Recent taps" sub="The reconciliation view: what the platform told us on each click, and where it got to.">
          <ClicksFeed />
        </Sec>

        <Sec>
          <Note testId="channels-compliance-note">
            Running finance ads in the UK is regulated. Approved wording, the claims you must avoid and the
            disclaimers that have to appear are all in your <b>Media kit</b> — and the desk will check a creative
            before you put spend behind it. Ask on Messages.
          </Note>
        </Sec>
      </PageBody>

      <AlertDialog open={!!pendingArchive} onOpenChange={(o) => !o && setPendingArchive(null)}>
        <AlertDialogContent data-testid="channel-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {pendingArchive?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The link stops working the moment you do this — anyone who taps the ad after that lands on an error,
              so pause the ads on {pendingArchive?.platform_label || "the platform"} first.
              {pendingArchive?.clicks ? ` The ${pendingArchive.clicks} tap${pendingArchive.clicks === 1 ? "" : "s"} already logged against it stay on your figures and exports.` : " Nothing recorded against it is lost."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="channel-archive-cancel">Leave it live</AlertDialogCancel>
            <AlertDialogAction onClick={archive} data-testid="channel-archive-confirm">Archive the channel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const linkBtn = {
  border: 0, background: "none", padding: 0, font: "inherit", cursor: "pointer",
  color: "var(--accent-text)", textDecoration: "underline", textDecorationStyle: "dotted",
};

/** What they spent on that channel this month — the only number we can't see ourselves. */
function SpendCell({ row, ym, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(row.spend_gbp || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(row.spend_gbp || ""); }, [row.spend_gbp]);

  if (row.id === "__none__") return <CellSub style={{ textAlign: "right" }}>—</CellSub>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/refer/campaign/spend", {
        link_id: row.id, year: ym.y, month: ym.m, spend_gbp: Number(val || 0),
      });
      toast.success(`${row.name}: ${gbp(Number(val || 0))} spend recorded`);
      setEditing(false);
      onSaved?.();
    } catch { toast.error("Couldn't save that"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); save(); }} data-testid={`channel-spend-form-${row.id}`}
            style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <input type="number" min="0" step="10" value={val} onChange={(e) => setVal(e.target.value)} autoFocus
               className="inp" style={{ width: 86, height: 30, padding: "0 8px", textAlign: "right", ...num }}
               data-testid={`channel-spend-input-${row.id}`} />
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy} data-testid={`channel-spend-save-${row.id}`}>Set</button>
      </form>
    );
  }
  return (
    <button type="button" onClick={() => setEditing(true)} style={{ ...linkBtn, textDecoration: "none", width: "100%", textAlign: "right" }}
            data-testid={`channel-spend-${row.id}`} title="What you spent on this channel this month">
      <CellMain style={{ ...num, color: row.spend_gbp ? "var(--ink)" : "var(--mut)", fontWeight: row.spend_gbp ? 700 : 500 }}>
        {row.spend_gbp ? gbp(row.spend_gbp) : "add spend"}
      </CellMain>
    </button>
  );
}

const EVENTS = [["submitted", "Application"], ["qualified", "Qualified"], ["funded", "Funded"]];
const EXPORTS = [["google", "Google Ads"], ["meta", "Meta"], ["tiktok", "TikTok"], ["linkedin", "LinkedIn"], ["all", "Everything"]];

function Postback({ data, ym }) {
  const pb = data?.postback || {};
  const [f, setF] = useState({ enabled: false, url: "", events: ["submitted", "qualified", "funded"] });
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [event, setEvent] = useState("funded");

  useEffect(() => {
    setF({ enabled: !!pb.enabled, url: pb.url || "", events: pb.events || ["submitted", "qualified", "funded"] });
  }, [pb.enabled, pb.url, pb.events?.join(",")]); // eslint-disable-line

  const loadLog = useCallback(() => {
    api.get("/refer/campaign/postback/log").then((r) => setLog(r.data.items || [])).catch(() => setLog([]));
  }, []);
  useEffect(() => { loadLog(); }, [loadLog]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/refer/campaign/postback", { enabled: f.enabled, url: f.url.trim() || null, events: f.events });
      toast.success(f.enabled ? "Postback on — we'll call that URL on every conversion" : "Postback off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that"); }
    finally { setBusy(false); }
  };

  const test = async () => {
    setBusy(true);
    try {
      const r = await api.post("/refer/campaign/postback/test");
      const s = r.data?.last?.status;
      toast[s >= 200 && s < 400 ? "success" : "error"](`Your endpoint answered ${s || "nothing"} · sent with cx_test=1 so you can filter it`);
      loadLog();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(false); }
  };

  const download = (platform) => {
    const q = `platform=${platform}&event=${event}&year=${ym.y}&month=${ym.m}`;
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = authToken() || "";
    fetch(`${base}/api/refer/campaign/export.csv?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error();
        const rows = r.headers.get("X-Rows");
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `connexis-${platform}-${event}-${ym.y}-${String(ym.m).padStart(2, "0")}.csv`;
        a.click();
        toast.success(rows === "0" ? "Nothing to export for that platform yet" : `${rows} conversion${rows === "1" ? "" : "s"} downloaded`);
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <Panel title="Conversions back to your ad accounts" testId="postback-panel"
           sub="Two ways, use either or both: a server-to-server postback the moment it happens, or the platform's own CSV upload at the end of the week."
           footer={<span>Macros you can use: <code>{"{click_id}"}</code> <code>{"{fbclid}"}</code> <code>{"{gclid}"}</code> <code>{"{ttclid}"}</code> <code>{"{li_fat_id}"}</code> <code>{"{sub1}"}</code> <code>{"{event}"}</code> <code>{"{value}"}</code> <code>{"{currency}"}</code> <code>{"{ref}"}</code>. We call it with a plain GET and log the answer.</span>}>
      <div style={{ display: "grid", gap: 14 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, cursor: "pointer" }}>
          <input type="checkbox" checked={f.enabled} onChange={(e) => setF((s) => ({ ...s, enabled: e.target.checked }))}
                 data-testid="postback-enabled" />
          <span>Send a postback when a conversion happens</span>
        </label>

        <Inp placeholder="https://your-tracker.com/postback?cid={click_id}&event={event}&value={value}"
             value={f.url} onChange={(e) => setF((s) => ({ ...s, url: e.target.value }))}
             data-testid="postback-url" />

        <div>
          <div className="rail-t" style={{ marginBottom: 8 }}>Fire it on</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {EVENTS.map(([k, label]) => {
              const on = f.events.includes(k);
              return (
                <button key={k} type="button" data-testid={`postback-event-${k}`}
                        onClick={() => setF((s) => ({ ...s, events: on ? s.events.filter((e) => e !== k) : [...s.events, k] }))}
                        className={`btn btn-${on ? "primary" : "ghost"} btn-sm`}>{label}</button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Btn kind="primary" sm disabled={busy} onClick={save} testId="postback-save">Save</Btn>
          <Btn sm disabled={busy || !f.enabled} onClick={test} testId="postback-test">Send a test</Btn>
          <span style={{ fontSize: 11.5, color: "var(--mut)" }} data-testid="postback-test-hint">
            A real call with a real payload — carrying <code>cx_test=1</code> so your platform can filter it
          </span>
          {pb.last_status ? (
            <St tone={pb.last_status >= 200 && pb.last_status < 400 ? "mint" : "red"} testId="postback-last">
              Last call {pb.last_status} · {ukWhen(pb.last_at)}
            </St>
          ) : null}
        </div>

        <Divider />

        <div>
          <div className="rail-t" style={{ marginBottom: 8 }}>Or download the upload file</div>
          <div style={{ marginBottom: 10 }}>
            <Chips items={EVENTS.map(([k, label]) => ({ key: k, label }))} value={event} onChange={setEvent} testPrefix="export-event" />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {EXPORTS.map(([k, label]) => (
              <Btn key={k} sm onClick={() => download(k)} testId={`export-${k}`}>{label} CSV</Btn>
            ))}
          </div>
        </div>

        {log.length > 0 && (
          <>
            <Divider />
            <div>
              <div className="rail-t" style={{ marginBottom: 8 }}>Last calls</div>
              <div style={{ display: "grid", gap: 6 }} data-testid="postback-log">
                {log.slice(0, 6).map((l) => (
                  <div key={l.id} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5, color: "var(--mut)" }}>
                    <St tone={l.status >= 200 && l.status < 400 ? "mint" : "red"}>{l.status || "no answer"}</St>
                    <span style={{ fontWeight: 600, color: "var(--ink)" }}>{l.event}</span>
                    <span>{l.reference || "—"}</span>
                    {l.test ? <St tone="slate" testId={`postback-log-test-${l.id}`}>test · cx_test=1</St> : null}
                    <span style={{ marginLeft: "auto", ...num }}>{ukWhen(l.at)}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

function ClicksFeed() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/refer/campaign/clicks?limit=40").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);

  const stageTone = { clicked: "slate", started: "ink", submitted: "blue", qualified: "blue", funded: "mint" };
  const rows = useMemo(() => items || [], [items]);

  return (
    <Panel pad={false} testId="clicks-feed"
           footer={<span>We keep the click id, your sub-ids and the UTMs — never the applicant's name, email or numbers. That is the whole point of the arrangement.</span>}>
      {items === null ? <Empty testId="clicks-loading">Reading the last taps…</Empty>
        : rows.length === 0 ? (
          <Empty testId="clicks-empty">No taps yet. They appear here within a second of someone clicking your ad.</Empty>
        ) : (
          <Tb testId="clicks-table" head={<>
            <Th style={{ width: "22%" }}>Channel</Th>
            <Th style={{ width: "24%" }}>What the platform told us</Th>
            <Th style={{ width: "14%" }}>Campaign</Th>
            <Th style={{ width: "14%" }}>Got to</Th>
            <Th style={{ width: "12%" }}>Case</Th>
            <Th className="r" style={{ width: "14%" }}>When</Th>
          </>}>
            {rows.map((c) => {
              const ids = Object.entries(c.click_ids || {});
              return (
                <tr key={c.id} data-testid={`click-row-${c.id}`}>
                  <Td className="lead">
                    <CellMain>{c.link_name || "—"}</CellMain>
                    <CellSub>{c.platform || "—"}</CellSub>
                  </Td>
                  <Td>
                    {ids.length ? ids.map(([k, v]) => (
                      <CellSub key={k} style={{ fontFamily: "ui-monospace, monospace" }}>{k}={String(v).slice(0, 26)}</CellSub>
                    )) : <CellSub>no click id on the URL</CellSub>}
                    {(c.subs || {}).sub1 ? <CellSub>sub1={c.subs.sub1}</CellSub> : null}
                  </Td>
                  <Td>
                    <CellMain>{(c.utm || {}).utm_campaign || "—"}</CellMain>
                    <CellSub>{(c.utm || {}).utm_content || (c.utm || {}).utm_medium || ""}</CellSub>
                  </Td>
                  <Td><St tone={stageTone[c.stage] || "slate"}>{c.stage || "clicked"}</St></Td>
                  <Td><CellMain>{c.reference || "—"}</CellMain></Td>
                  <Td className="r"><CellSub style={{ textAlign: "right" }}>{ukWhen(c.at)}</CellSub></Td>
                </tr>
              );
            })}
          </Tb>
        )}
    </Panel>
  );
}
